import { Table, RowData, flexRender, Header, Row } from '@tanstack/react-table';
import { Fragment, useEffect, useRef } from 'react';
import { useMeasure } from 'react-use';
import { TableSortAscButton, TableSortDescButton } from '../tableSortButtons';
import { StickyOptions } from '../tableTypes';
import { DrillthroughTableInjectableRow } from './drillthrough-table.types';
import Blur from 'features/blur/blur';

interface DrillthroughTableProps<T extends RowData> {
  table: Table<T>;
  componentId: string;
  heightClass?: string;
  cellClasses?: string;
  totalRow?: DrillthroughTableInjectableRow;
  injectableRows: DrillthroughTableInjectableRow[];
}

const tryGetStickyOptions = (meta: any): string => {
  const stickyOptions: StickyOptions | undefined = meta?.stickyOptions;

  return stickyOptions?.isEnabled ? `sticky ${stickyOptions.side}-0  z-20` : '';
};

const moveStickyRow = (scrollPosition: number, threshold: number, ref: any) => {
  if (scrollPosition >= threshold) {
    ref!.current.style.transform = `translate3d(0, ${scrollPosition - 5}px, 0)`;
  } else {
    ref!.current.style.transform = 'translate3d(0, 0, 0)';
  }
};

export function DrillthroughTable<T extends RowData>({
  table,
  componentId,
  heightClass,
  cellClasses,
  totalRow,
  injectableRows
}: DrillthroughTableProps<T>) {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const hasResized = useRef<boolean>(false);

  const getCustomHandlerResizer = (header: Header<T, unknown>) => {
    if (!hasResized.current) {
      hasResized.current = true;
    }
    return header.getResizeHandler();
  };

  const headerRef = useRef<any>(null);
  const totalRef = useRef<any>(null);
  const scrollRef = useRef<any>(null);
  useEffect(() => {
    const handleScroll = () => {
      const content = scrollRef.current;
      const scrollPosition = +content.scrollTop;
      const headerThreshold = 45;
      moveStickyRow(scrollPosition, headerThreshold, headerRef);
      if (totalRow) {
        const totalThreshold = headerThreshold * 2;
        moveStickyRow(scrollPosition, totalThreshold, totalRef);
      }
    };

    scrollRef.current.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [totalRow]);

  return (
    <div
      key={componentId + '_drilldown-table'}
      ref={ref}
      className={heightClass}
    >
      <div ref={scrollRef} className={`overflow-scroll ${heightClass}`}>
        <table
          className="table-fixed"
          {...{
            style: {
              width:
                table.getCenterTotalSize() > width
                  ? table.getCenterTotalSize()
                  : width
            }
          }}
        >
          <thead
            className={`relative first:text-left capitalize z-30 bg-white text-sm whitespace-nowrap text-gray`}
            id="drilldown-header"
            ref={headerRef}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id + '_header_group'}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id + '_header'}
                    className={`relative font-normal px-6 pl-2 p-2 py-3 w-fit bg-white ${tryGetStickyOptions(
                      header.column.columnDef.meta
                    )}  ${cellClasses ?? ' px-6 p-2 py-4 '}`}
                    colSpan={header.colSpan}
                    style={{
                      width:
                        !header.column.getCanResize() ||
                        (!header.column.getIsResizing() &&
                          !hasResized.current &&
                          header.column.columnDef.size)
                          ? header.column.columnDef.size
                          : header.getSize()
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          className={`relative ${
                            !header.isPlaceholder && header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : ''
                          } `}
                          onClick={
                            !header.isPlaceholder && header.column.getCanSort()
                              ? header.column.getToggleSortingHandler()
                              : () => {}
                          }
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <div className="absolute top-1/3 right-6 rounded select-none touch-none cursor-pointer">
                            {{
                              asc: <TableSortAscButton />,
                              desc: <TableSortDescButton />
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        </div>
                        {header.column.getCanResize() && (
                          <span
                            onMouseDown={getCustomHandlerResizer(header)}
                            onTouchStart={getCustomHandlerResizer(header)}
                            className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none group"
                          >
                            <div
                              className={`absolute left-1/2 top-1/4 h-1/2 w-0.5 rounded select-none touch-none bg-off-navy group-hover:bg-navy cursor-col-resize ${
                                header.column.getIsResizing()
                                  ? 'isResizing'
                                  : ''
                              }`}
                            ></div>
                          </span>
                        )}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {totalRow ? (
              renderDrillthroughRow(
                'total',
                'bg-white relative z-30',
                cellClasses!,
                totalRow,
                totalRef
              )
            ) : (
              <></>
            )}
            {table.getRowModel()?.rows?.map((row, rowIndex) => {
              const backgroundColour = getRowColour(rowIndex);

              return (
                <Fragment key={row.id + '_row'}>
                  <tr className={backgroundColour}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id + '_cell'}
                        className={`text-left break-word align-text-top p-2 px-6 pl-2 ${backgroundColour} ${tryGetStickyOptions(
                          cell.column.columnDef.meta
                        )} ${cellClasses ?? 'truncate'}`}
                        style={{
                          width: cell.column.getSize()
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                  {renderDrillthroughRows(
                    row,
                    injectableRows,
                    rowIndex,
                    cellClasses
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const renderDrillthroughRows = (
  row: Row<any>,
  allInjectableRows: DrillthroughTableInjectableRow[],
  rowIndex: number,
  cellClasses?: string
) => {
  const drillthroughRows: DrillthroughTableInjectableRow[] = [];
  addRowChildren(allInjectableRows, row.original.id, drillthroughRows);

  return drillthroughRows.map((drillthroughRow, drillthroughRowIndex) => {
    const rowColourIndex = rowIndex + drillthroughRowIndex + 1;
    const rowColour = getRowColour(rowColourIndex);

    const previousDrillthroughHasChildren =
      drillthroughRowIndex === 0
        ? !drillthroughRow.hasChildren
        : drillthroughRows[drillthroughRowIndex - 1].hasChildren;

    const nextDrillthroughHasChildren =
      drillthroughRowIndex === drillthroughRows.length - 1
        ? !drillthroughRow.hasChildren
        : drillthroughRows[drillthroughRowIndex + 1].hasChildren;

    const borderTop =
      !drillthroughRow.hasChildren && previousDrillthroughHasChildren
        ? `border-t border-t-tertiary-600 ${drillthroughRowIndex}`
        : '';
    const borderBottom =
      !drillthroughRow.hasChildren && nextDrillthroughHasChildren
        ? `border-b border-b-tertiary-600 ${drillthroughRowIndex}`
        : '';

    const rowClasses = `${rowColour} ${borderTop} ${borderBottom}`;

    return renderDrillthroughRow(
      row.id,
      rowClasses,
      cellClasses!,
      drillthroughRow,
      null
    );
  });
};

function renderDrillthroughRow(
  rowId: string,
  rowClasses: string,
  cellClasses: string,
  drillthroughRow: DrillthroughTableInjectableRow,
  ref: any
): JSX.Element {
  return (
    <tr
      key={`${rowId}-drillthrough-${drillthroughRow.rowId}`}
      className={rowClasses}
      ref={ref}
    >
      {drillthroughRow.cells.map((cell, cellIndex) => (
        <td
          key={`${rowId}-drillthrough-${drillthroughRow.rowId}-${cell.cellId}`}
          className={`text-left break-word p-2 px-6 pl-2 ${
            cellIndex === 0 ? 'sticky left-0 z-20' : ''
          } ${cellClasses ?? 'overflow-hidden'} ${rowClasses}`}
        >
          {cell.isBlurred ? (
            <Blur>
              <div className={`ml-${drillthroughRow.depth}`}>
                {cell.element}
              </div>
            </Blur>
          ) : (
            <div className={`ml-${drillthroughRow.depth}`}>{cell.element}</div>
          )}
        </td>
      ))}
    </tr>
  );
}

const addRowChildren = (
  allInjectableRows: DrillthroughTableInjectableRow[],
  parentId: string,
  drillthroughRows: DrillthroughTableInjectableRow[]
): void => {
  const children = allInjectableRows.filter((r) => r.parentId === parentId);

  for (const child of children) {
    drillthroughRows.push(child);

    addRowChildren(allInjectableRows, child.rowId, drillthroughRows);
  }
};

function getRowColour(rowIndex: number): string {
  return rowIndex % 2 === 0 ? 'bg-neutral-100' : 'bg-white';
}
